import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Image as ImageIcon, Layers } from "lucide-react";
import { API_URL } from "@/utils/apiConfig";

export function CategoriasTab({ categories, onRefresh }) {
  const { authAxios, user } = useAuth();
  const BACKEND_URL = API_URL;
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [position, setPosition] = useState(0);
  const [imageUrl, setImageUrl] = useState("");
  const [active, setActive] = useState(true);
  const [showInDelivery, setShowInDelivery] = useState(true);
  const [showInTable, setShowInTable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const resetForm = () => {
    setName("");
    setDescription("");
    setPosition(0);
    setImageUrl("");
    setActive(true);
    setShowInDelivery(true);
    setShowInTable(true);
    setEditingCategory(null);
  };

  const openCreate = () => {
    resetForm();
    setPosition(categories.length);
    setShowForm(true);
  };

  const openEdit = (cat) => {
    setEditingCategory(cat);
    setName(cat.name || "");
    setDescription(cat.description || "");
    setPosition(cat.position ?? cat.order ?? 0);
    setImageUrl(cat.image_url || "");
    setActive(cat.active !== false);
    setShowInDelivery(cat.show_in_delivery !== false);
    setShowInTable(cat.show_in_table !== false);
    setShowForm(true);
  };

  const handleImageUpload = async (file) => {
    if (!file.type.startsWith("image/")) { toast.error("Solo imagenes"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await authAxios.post("/menu/upload-media", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(res.data.url);
      toast.success("Imagen subida");
    } catch { toast.error("Error al subir imagen"); }
    setUploading(false);
  };

  const saveCategory = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Nombre es obligatorio"); return; }
    setLoading(true);
    try {
      const slug = name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
      const link = `${BACKEND_URL}/m/${user?.restaurant_id}/#/categoria/${slug}`;

      if (editingCategory) {
        await authAxios.put(`/menu/categories/${editingCategory.id}`, {
          name: name.trim(),
          description: description.trim(),
          position,
          image_url: imageUrl,
          active,
          link,
          show_in_delivery: showInDelivery,
          show_in_table: showInTable,
        });
        toast.success("Categoria actualizada");
      } else {
        await authAxios.post(`/menu/categories?name=${encodeURIComponent(name.trim())}&position=${position}&link=${encodeURIComponent(link)}&description=${encodeURIComponent(description)}&image_url=${encodeURIComponent(imageUrl)}&active=${active}&show_in_delivery=${showInDelivery}&show_in_table=${showInTable}`);
        toast.success("Categoria creada");
      }
      resetForm();
      setShowForm(false);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setLoading(false);
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("¿Eliminar esta categoria? Los productos se quedaran sin categoria.")) return;
    try { await authAxios.delete(`/menu/categories/${id}`); toast.success("Eliminada"); onRefresh(); } catch { toast.error("Error"); }
  };

  return (
    <div data-testid="categorias-tab">
      <Button variant="outline" className="border-emerald-300 text-emerald-700 font-bold uppercase mb-4" onClick={openCreate} data-testid="new-category-btn">
        <Plus className="w-4 h-4 mr-2" /> NUEVA CATEGORIA
      </Button>
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-gray-500 text-xs uppercase">
              <th className="text-left p-3 font-medium w-12"></th>
              <th className="text-left p-3 font-medium">Nombre</th>
              <th className="text-left p-3 font-medium">Descripcion</th>
              <th className="text-center p-3 font-medium">Orden</th>
              <th className="text-center p-3 font-medium">Canal</th>
              <th className="text-center p-3 font-medium">Estado</th>
              <th className="p-3 w-24">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {categories.sort((a, b) => (a.position ?? a.order ?? 0) - (b.position ?? b.order ?? 0)).map(cat => (
              <tr key={cat.id} className="hover:bg-gray-50" data-testid={`category-row-${cat.id.slice(-6)}`}>
                <td className="p-3">
                  {cat.image_url ? (
                    <img src={cat.image_url.startsWith("/api/") ? `${API_URL}${cat.image_url}` : cat.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center"><Layers className="w-4 h-4 text-gray-400" /></div>
                  )}
                </td>
                <td className="p-3"><span className="text-emerald-700 font-medium">{cat.name}</span></td>
                <td className="p-3 text-gray-500 text-sm max-w-xs truncate">{cat.description || "-"}</td>
                <td className="p-3 text-center"><Badge variant="outline"># {cat.position ?? cat.order ?? 0}</Badge></td>
                <td className="p-3 text-center">
                  {cat.show_in_delivery === false && cat.show_in_table === false ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700">Somente POS</span>
                  ) : (
                    <span className="flex gap-1 justify-center flex-wrap">
                      {cat.show_in_delivery !== false && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-600">Delivery</span>}
                      {cat.show_in_table !== false && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-600">Mesa</span>}
                    </span>
                  )}
                </td>
                <td className="p-3 text-center">
                  <Badge className={cat.active !== false ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}>
                    {cat.active !== false ? "Activa" : "Inactiva"}
                  </Badge>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-500 hover:text-blue-700 hover:bg-blue-50" onClick={() => openEdit(cat)} data-testid={`edit-category-${cat.id.slice(-6)}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => deleteCategory(cat.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {categories.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-gray-400">No hay categorias. Crea la primera!</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); resetForm(); } else setShowForm(true); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="uppercase font-bold">{editingCategory ? "Editar" : "Nueva"} Categoria</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveCategory} className="space-y-4 mt-2">
            <div>
              <Label>Nombre *</Label>
              <Input value={name} onChange={e => setName(e.target.value)} required className="mt-1" placeholder="Ej: Entradas, Sushi, Postres" data-testid="category-name" />
            </div>
            <div>
              <Label>Descripcion</Label>
              <Input value={description} onChange={e => setDescription(e.target.value)} className="mt-1" placeholder="Descripcion opcional de la categoria" data-testid="category-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Posicion</Label>
                <Input type="number" min={0} value={position} onChange={e => setPosition(Number(e.target.value))} className="mt-1" data-testid="category-position" />
                <p className="text-xs text-gray-400 mt-1">0 = primero en el menu</p>
              </div>
              <div>
                <Label>Estado</Label>
                <Select value={active ? "active" : "inactive"} onValueChange={v => setActive(v === "active")}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Activa</SelectItem>
                    <SelectItem value="inactive">Inactiva</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Visibilidad por Canal</Label>
              <div className="mt-2 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={showInDelivery} onCheckedChange={setShowInDelivery} data-testid="category-show-delivery" />
                  <span className="text-sm">Mostrar en Delivery / Menu Publico</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={showInTable} onCheckedChange={setShowInTable} data-testid="category-show-table" />
                  <span className="text-sm">Mostrar en Mesa (QR)</span>
                </label>
                {!showInDelivery && !showInTable && (
                  <p className="text-xs text-amber-600 font-medium">Somente POS — nao aparece no menu publico nem na mesa</p>
                )}
              </div>
            </div>
            <div>
              <Label>Imagen (opcional)</Label>
              <div
                className="mt-1 border-2 border-dashed border-gray-200 rounded-lg p-3 text-center cursor-pointer hover:border-emerald-400 transition"
                onClick={() => !uploading && fileInputRef.current?.click()}
              >
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && handleImageUpload(e.target.files[0])} />
                {uploading ? (
                  <div className="flex items-center justify-center gap-2 py-2"><Loader2 className="w-4 h-4 animate-spin" /> Subiendo...</div>
                ) : imageUrl ? (
                  <div className="relative group">
                    <img src={imageUrl.startsWith("/api/") ? `${API_URL}${imageUrl}` : imageUrl} alt="" className="w-full h-24 object-cover rounded" />
                    <button type="button" onClick={e => { e.stopPropagation(); setImageUrl(""); }} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">x</button>
                  </div>
                ) : (
                  <div className="py-2"><ImageIcon className="w-6 h-6 mx-auto text-gray-300 mb-1" /><p className="text-xs text-gray-400">Click para subir imagen</p></div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>Cancelar</Button>
              <Button type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-category-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : editingCategory ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
